import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FluxButton, FluxCheckbox } from '@meltwater/flux-web-components-react';
import { Stack, Tooltip } from '@mui/material';
import styles from './UsersAndWorkspaces.module.css';
import CompanyServices from '../../services/company.services';
import { useCompanyId } from '../../services/CompanyidProvider';
import { useUserData } from './UsersAndWorkspaces';
import { exportUsers, trackExport } from '../../services/exportUtils';
import DataTable from '../../components/DataTable';

// const formatDate = (dateString) => {
//     if (!dateString) return '';
//     const date = new Date(dateString);
//     return date.toLocaleDateString('en-US', {
//         month: 'short',
//         day: 'numeric',
//         year: 'numeric'
//     });
// };

const GetUsersData = ({ load, setActiveTab }) => {
    const { companyId } = useCompanyId();
    const { 
        userData, 
        setUserData, 
        usersData, 
        updateUsersData, 
        setLoading 
    } = useUserData();
    const [openSnackBar, setOpenSnackBar] = useState(false);
    const [feedbackMsg, setFeedbackMsg] = useState('');
    const [severity, setSeverity] = useState('info');
    const [isLoading, setIsLoading] = useState(!userData && !usersData.isLoaded);
    const [includeInternal, setIncludeInternal] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const loadingTimerRef = useRef(null);
    const apiAttemptedRef = useRef(false);
    const landingPageNavRef = useRef(false);
    
    // User guide steps (add more as needed)
    const userGuideSteps = useMemo(() => [
        {
            title: 'User Management',
            text: 'This guide will help you navigate the User Management features.',
            element: null,
        },
        {
            title: 'Search Users',
            text: 'Use the search box to quickly find users by name, email, or ID.',
            element: '.mrt-input',
            position: { top: 20, left: 200 }
        },
        {
            title: 'Column Visibility & Table Settings',
            text: 'Top right of the table, click those button to show or hide specific columns in the table and adjust table settings.',
            element: '.mrt-button.mrt-dropdown-button',
            position: { top: 0, right: 80 }
        },
        {
            title: 'Export Users',
            text: 'You can export all users or selected users using these buttons.',
            element: '.MuiButtonBase-root.FluxButton-root',
            position: { top: 0, left: 100 }
        },
        {
            title: 'Include Internal Users',
            text: 'Toggle this checkbox to include internal Meltwater users in exports.',
            element: '.MuiButtonBase-root.FluxCheckbox-root',
            position: { top: 0, left: 400 }
        },
        {
            title: 'User Statistics',
            text: 'View the count of external, internal, and total users here.',
            element: 'div[style*="display: flex"][style*="align-items: center"]',
            position: { top: 0, right: 100 }
        },
        {
            title: 'User Selection',
            text: 'Select users with checkboxes to perform operations on specific users.',
            element: '.mrt-row-select-checkbox',
            position: { top: 100, left: 30 }
        }
    ], []);

    useEffect(() => {
        async function fetchData() {
            // Prevent API call if error occurred from landing page navigation
            if (hasError && landingPageNavRef.current && apiAttemptedRef.current) {
                return;
            }

            if (!userData && !usersData.isLoaded && !apiAttemptedRef.current) {
                apiAttemptedRef.current = true; // Mark that we've attempted the API call
                
                try {
                    setIsLoading(true);
                    setLoading(true);
                    setLoadingMessage('Fetching users...');
                    loadingTimerRef.current = setTimeout(() => {
                        setLoadingMessage('Please wait, it takes longer than usual for larger companies...');
                    }, 15000);
                    
                    console.log("Initiating getUsersData call for company:", companyId);
                    const response = await CompanyServices.getUsersData(companyId);
                    console.log("getUsersData call successful");
                    
                    const { users, workspaces, metadata } = response.data;
                    setUserData(response.data);
                    updateUsersData({
                        users,
                        workspaces,
                        metadata,
                        isLoaded: true
                    });
                    setFeedbackMsg(`Successfully loaded ${users.length} users.`);
                    setSeverity('success');
                    setOpenSnackBar(true);
                    setHasError(false);
                } catch (err) {
                    console.error("Error in getUsersData API call:", err);
                    setOpenSnackBar(true);
                    const errorMsg = err.message || 'Unknown error';
                    setFeedbackMsg(`Error loading data: ${errorMsg}, Please refresh or contact support.`);
                    setErrorMessage(errorMsg);
                    setSeverity('error');
                    setHasError(true);
                } finally {
                    if (loadingTimerRef.current) {
                        clearTimeout(loadingTimerRef.current);
                        loadingTimerRef.current = null;
                    }
                    setLoadingMessage('');
                    setIsLoading(false);
                    setLoading(false);
                }
            } else if (userData && !usersData.isLoaded) {
                const { users, workspaces, metadata } = userData;
                updateUsersData({
                    users,
                    workspaces,
                    metadata,
                    isLoaded: true
                });
                setIsLoading(false);
            }
        }
        
        if (load) {
            fetchData();
        }
        
        return () => {
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
                loadingTimerRef.current = null;
            }
        };
    }, [companyId, load, userData, usersData.isLoaded, setLoading, setUserData, updateUsersData, hasError]);

    const handleExport = async (selectedRows = null) => {
        try {
            setIsLoading(true);
            
            const baseUsers = selectedRows 
                ? selectedRows.map(row => row.original)
                : usersData.users;
                
            const filteredUsers = includeInternal 
                ? baseUsers 
                : baseUsers.filter(user => !user.user.email?.includes('@meltwater.com') && !user.user.isInternal);
        
            await trackExport(
                companyId,
                filteredUsers,
                'user',
                {
                    type: selectedRows ? 'Selected' : 'All',
                    totalUsers: usersData.users.length,
                    includeInternal: includeInternal // parameter name remains the same in the component
                }
            );
            
            // Pass the workspace names mapping to the exportUsers function
            const result = await exportUsers(filteredUsers, companyId, usersData.workspaces);
            const message = result.count > 0
                ? `Successfully exported ${result.count} users`
                : 'Generated empty CSV template (no users match the current filters)';
                
            setFeedbackMsg(message);
            setSeverity(result.count > 0 ? 'success' : 'info');
        } catch (err) {
            console.error('Export error:', err);
            setFeedbackMsg('Export failed. Please try again or contact support.');
            setSeverity('error');
        } finally {
            setIsLoading(false);
            setOpenSnackBar(true);
        }
    };

    const handleCloseSnackBar = () => {
        setOpenSnackBar(false);
    };

    const refreshData = async () => {
        try {
            setIsLoading(true);
            apiAttemptedRef.current = false; // Reset API attempt for refresh
            setHasError(false); // Clear any previous errors
            setLoadingMessage('Refreshing user data...');
            loadingTimerRef.current = setTimeout(() => {
                setLoadingMessage('Please wait, it takes longer than usual for larger companies...');
            }, 15000);
            
            console.log("Initiating getUsersData refresh for company:", companyId);
            const response = await CompanyServices.getUsersData(companyId, 'refresh');
            console.log("getUsersData refresh successful");
            
            const { users, workspaces, metadata } = response.data;
            updateUsersData({
                users: users,
                workspaces: workspaces,
                metadata: {
                    total: metadata.total,
                    internal: metadata.internal,
                    external: metadata.external
                },
                isLoaded: true
            });
            setFeedbackMsg('Data refreshed successfully');
            setSeverity('success');
            setOpenSnackBar(true);
        } catch (err) {
            console.error("Error refreshing user data:", err);
            setHasError(true); // Track error state
            const errorMsg = err.message || 'Unknown error';
            setErrorMessage(errorMsg);
            setFeedbackMsg(`Error refreshing data: ${errorMsg}, please try again or contact support.`);
            setSeverity('error');
            setOpenSnackBar(true);
        } finally {
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
                loadingTimerRef.current = null;
            }
            setIsLoading(false);
            setLoadingMessage('');
        }
    };

    const renderToolbarActions = ({ table }) => {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <FluxButton
                    tint="accent"
                    type="filled"
                    onClick={() => handleExport(table.getSelectedRowModel().rows)}
                    disabled={!table.getSelectedRowModel().rows.length}
                >
                    Export Selected Users 
                </FluxButton>
                <FluxButton
                    tint="primary"
                    type="filled"
                    onClick={() => handleExport()}
                >
                    Export All Users
                </FluxButton>
                <FluxButton
                    tint="secondary"
                    type="filled"
                    onClick={refreshData}
                >
                    Refresh Data
                </FluxButton>
                <FluxCheckbox
                    checked={includeInternal}
                    onFluxChange={(e) => setIncludeInternal(e.target.checked)}
                >
                    Include Internal Users
                </FluxCheckbox> 
                <span>|</span>
                <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    color: '#666',
                    fontSize: '0.9rem',
                    fontWeight: 500
                }}>
                    (<span style={{marginRight: '4px' }}>
                        {usersData.metadata?.external || 0} external
                    </span>
                    ,
                    <span style={{ marginLeft: '4px' }}>
                        {usersData.metadata?.internal || 0} internal
                    </span>
                    <span> , </span> <span style={{ marginLeft: '4px' }}>Total: {usersData.metadata?.total || 0}</span>)
                </div>
            </Stack>
        );
    };

    const columns = useMemo(
        () => [
            { 
                accessorKey: 'user._id', 
                header: 'ID',
                enableGlobalFilter: true 
            },
            { 
                accessorKey: 'user.firstName', 
                header: 'First Name',
                enableGlobalFilter: true 
            },
            { 
                accessorKey: 'user.lastName', 
                header: 'Last Name',
                enableGlobalFilter: true 
            },
            { 
                accessorKey: 'user.email', 
                header: 'Email',
                enableGlobalFilter: true 
            },
            {
                accessorKey: 'user.pending',
                header: 'Status',
                enableGlobalFilter: true,
                Cell: ({ cell }) => (
                    <span>{cell.getValue() === true ? 'Pending' : 'Active'}</span>
                )
            },
            {
                accessorKey: 'user.isInternal',
                header: 'isInternal',
                Cell: ({ cell }) => (
                    <span>{cell.getValue() === true ? 'Yes' : 'No'}</span>
                )
            },
            { 
                accessorKey: 'user.created', 
                header: 'Date Created',
                enableGlobalFilter: false,
                // Cell: ({ cell }) => formatDate(cell.getValue())
            },
            { 
                accessorKey: 'user.modified', 
                header: ' Date Modified',
                enableGlobalFilter: false,
                // Cell: ({ cell }) => formatDate(cell.getValue())
            },
            { 
                accessorKey: 'user.timezone', 
                header: 'Timezone',
                enableGlobalFilter: true 
            },
            {
                accessorKey: 'user.activeWorkspaceId',
                header: 'Default Workspace',
                enableGlobalFilter: true,
                Cell: ({ cell }) => (
                    <span>{usersData.workspaces[cell.getValue()] || ''}</span>
                )
            },
            {
                accessorKey: 'workspaces',
                header: 'All Workspaces',
                enableGlobalFilter: true,
                Cell: ({ row }) => {
                    const workspaces = row.original.workspaces;
                    if (workspaces.length <= 3) {
                        return <span>{workspaces.join(', ')}</span>;
                    }
                    return (
                        <Tooltip title={workspaces.join(', ')}>
                            <span>
                                {workspaces.slice(0, 3).join(', ')} ...
                            </span>
                        </Tooltip>
                    );
                }
            },
        ],
        [usersData.workspaces]
    );

    return (
        <div className={styles.Search}>
            <DataTable 
                columns={columns}
                data={usersData.isLoaded ? usersData.users : []}
                isLoading={isLoading}
                loadingMessage={loadingMessage || 'Please wait, fetching users...'}
                hasError={hasError && !usersData.isLoaded}
                errorMessage={errorMessage}
                errorTitle="Error Loading User Data"
                onBackClick={() => setActiveTab(null)}
                renderToolbarActions={renderToolbarActions}
                backTooltip="Return to Dashboard"
                showGuide={false}
                guideSteps={userGuideSteps}
                snackbarProps={{
                    open: openSnackBar,
                    onClose: handleCloseSnackBar,
                    message: feedbackMsg,
                    severity: severity,
                    autoHideDuration: 10000
                }}
                tableProps={{
                    muiSearchTextFieldProps: {
                        placeholder: 'Search users...',
                        variant: 'standard',
                        sx: { width: '300px' }
                    },
                    initialState: {
                        density: 'compact',
                        columnVisibility: {
                            'user.created':false,
                            'user.modified': false,
                        },
                        pagination: { pageSize: 15 }
                    },
                }}
            />
        </div>
    );
};

export default GetUsersData;